import React, { useState, useEffect } from 'react';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { CategoryAttribute } from '../types/foundObject';
import { toYmdForDateInput } from '../utils/toYmdForDateInput';
import MobileAttributeSelector from './MobileAttributeSelector';
import ColorSelectField from './ColorSelectField';
import {
  findOtherSelectOption,
  getOtherSpecifyFieldLabel,
  getSelectOtherState,
  isCustomOtherSelectValue,
  isOtherSelectOption,
} from '../utils/selectOtherOption';
import {
  resolveConditionalDependsOnValue,
  resolveConditionalSelectLabel,
  resolveConditionalSelectOptions,
} from '../utils/categoryAttributeFilters';

interface ConditionalAttributeFieldProps {
  attributeKey: string;
  attribute: CategoryAttribute;
  value: string;
  dependentValues: Record<string, string>;
  onChange: (value: string) => void;
}

const isHexColorValue = (candidate: string) => /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(candidate.trim());

const ConditionalAttributeField: React.FC<ConditionalAttributeFieldProps> = ({
  attributeKey,
  attribute,
  value,
  dependentValues,
  onChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [availableOptions, setAvailableOptions] = useState<string[]>([]);

  useEffect(() => {
    if (attribute.type === 'conditional_select') {
      const newOptions = resolveConditionalSelectOptions(attributeKey, attribute, dependentValues);
      setAvailableOptions(newOptions);
      if (value && !newOptions.includes(value) && !isCustomOtherSelectValue(value, newOptions)) {
        onChange('');
      }
    } else if (attribute.type === 'select' && Array.isArray(attribute.options)) {
      setAvailableOptions(attribute.options);
      if (value && !attribute.options.includes(value) && !isCustomOtherSelectValue(value, attribute.options)) {
        onChange('');
      }
    } else if (attribute.type === 'color_select' && attribute.colorOptions) {
      const colorNames = attribute.colorOptions.map((option) => option.name);
      setAvailableOptions(colorNames);
      if (value && !colorNames.includes(value) && !isHexColorValue(value)) {
        onChange('');
      }
    }
  }, [attribute, attributeKey, dependentValues, value, onChange]);

  const dependsOnValue =
    attribute.type === 'conditional_select'
      ? resolveConditionalDependsOnValue(attributeKey, attribute, dependentValues)
      : '';

  const displayLabel =
    attribute.type === 'conditional_select'
      ? resolveConditionalSelectLabel(attribute, dependsOnValue)
      : attribute.label;

  const handleMobileOpen = () => {
    setMobileOpen(true);
  };

  const handleMobileClose = () => {
    setMobileOpen(false);
  };

  const handleMobileValueChange = (newValue: string) => {
    onChange(newValue);
    setMobileOpen(false);
  };

  if (attribute.type === 'conditional_select' && availableOptions.length === 0) {
    return null;
  }

  if (attribute.type === 'select' || attribute.type === 'conditional_select') {
    const { selectValue, showOtherSpecify, otherSpecifyValue } = getSelectOtherState(value || '', availableOptions);

    const handleSelectChange = (nextValue: string) => {
      onChange(nextValue);
    };

    const handleOtherSpecifyChange = (customValue: string) => {
      if (!customValue.trim()) {
        onChange(findOtherSelectOption(availableOptions) || 'Autre');
        return;
      }
      onChange(customValue);
    };

    const otherSpecifyFieldLabel = getOtherSpecifyFieldLabel(displayLabel);

    if (isMobile) {
      return (
        <Box>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleMobileOpen}
            sx={{
              justifyContent: 'flex-start',
              textAlign: 'left',
              height: '56px',
            }}
          >
            {value || `Sélectionner ${displayLabel.toLowerCase()}`}
          </Button>

          <MobileAttributeSelector
            open={mobileOpen}
            onClose={handleMobileClose}
            attribute={attribute}
            value={value}
            onValueChange={handleMobileValueChange}
            dependsOnValue={dependsOnValue}
            title={displayLabel}
          />

          {showOtherSpecify && isOtherSelectOption(value || '') ? (
            <TextField
              fullWidth
              label={otherSpecifyFieldLabel}
              required={attribute.required}
              value={otherSpecifyValue}
              onChange={(e) => handleOtherSpecifyChange(e.target.value)}
              sx={{ mt: 1.5 }}
            />
          ) : null}
        </Box>
      );
    }

    return (
      <Box>
        <FormControl fullWidth required={attribute.required}>
          <InputLabel>{displayLabel}</InputLabel>
          <Select
            value={selectValue}
            onChange={(e) => handleSelectChange(e.target.value)}
            label={displayLabel}
          >
            {availableOptions.map((option, index) => (
              <MenuItem key={`${attributeKey}-${option}-${index}`} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {showOtherSpecify ? (
          <TextField
            fullWidth
            label={otherSpecifyFieldLabel}
            required={attribute.required}
            value={otherSpecifyValue}
            onChange={(e) => handleOtherSpecifyChange(e.target.value)}
            sx={{ mt: 1.5 }}
          />
        ) : null}
      </Box>
    );
  }

  const ph = attribute.placeholder?.trim() ? attribute.placeholder.trim() : undefined;
  const labelShrink = !!ph;

  switch (attribute.type) {
    case 'string':
      return (
        <TextField
          fullWidth
          label={attribute.label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={attribute.required}
          placeholder={ph}
          InputLabelProps={labelShrink ? { shrink: true } : undefined}
        />
      );

    case 'text':
      return (
        <TextField
          fullWidth
          multiline
          minRows={4}
          maxRows={16}
          label={attribute.label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={attribute.required}
          placeholder={ph}
          InputLabelProps={{ shrink: true }}
        />
      );

    case 'number':
      return (
        <TextField
          fullWidth
          type="number"
          label={attribute.label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={attribute.required}
        />
      );

    case 'date': {
      const dateValue = toYmdForDateInput(value) ?? '';
      return (
        <TextField
          fullWidth
          type="date"
          label={attribute.label}
          value={dateValue}
          onChange={(e) => onChange(e.target.value)}
          required={attribute.required}
          InputLabelProps={{ shrink: true }}
        />
      );
    }

    case 'color_select':
      return (
        <ColorSelectField
          label={attribute.label}
          required={attribute.required}
          value={value}
          onChange={onChange}
          options={(attribute.colorOptions ?? []).map((colorOption) => ({
            value: colorOption.name,
            label: colorOption.name,
            hex: colorOption.hex,
          }))}
        />
      );

    default:
      return null;
  }
};

export default ConditionalAttributeField;
